import React, { useState } from "react";
import { Button, cn } from "@lemonadetraining/web-ui";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import YouTubeEmbed from "../../video/YouTubeEmbed";

interface VideoCarouselProps {
  youTubeIDs: string[];
  title?: string;
}

const VideoCarousel: React.FC<VideoCarouselProps> = ({ youTubeIDs, title }) => {
  // Cards saved before links were validated can hold null or "" in the list.
  const ids = youTubeIDs.filter((id): id is string => typeof id === "string" && id !== "");
  const [page, setPage] = useState(0);

  if (ids.length === 0) return null;

  const total = ids.length;
  const current = Math.min(page, total - 1);
  const id = ids[current];
  const name = title?.trim() || "Workout";
  const go = (next: number) => setPage((next + total) % total);

  return (
    <div className="flex w-full max-w-xl flex-col gap-2" data-testid="video-carousel">
      <YouTubeEmbed
        key={id}
        id={id}
        title={total > 1 ? `${name} video ${current + 1} of ${total}` : `${name} video`}
      />
      {total > 1 && (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-11"
            aria-label="Previous video"
            onClick={() => go(current - 1)}
          >
            <IconChevronLeft />
          </Button>
          <div className="flex" role="group" aria-label="Videos">
            {ids.map((videoID, i) => (
              <button
                key={`${videoID}-${i}`}
                type="button"
                aria-label={`Video ${i + 1}`}
                aria-current={i === current ? "true" : undefined}
                onClick={() => setPage(i)}
                className="grid size-11 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "block h-2 rounded-full transition-all",
                    i === current ? "w-5 bg-foreground" : "w-2 bg-muted-foreground/50"
                  )}
                />
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-11"
            aria-label="Next video"
            onClick={() => go(current + 1)}
          >
            <IconChevronRight />
          </Button>
          <span role="status" className="ml-1 text-xs text-muted-foreground tabular-nums">
            Video {current + 1} of {total}
          </span>
        </div>
      )}
    </div>
  );
};

export default VideoCarousel;
